import { AlertTriangle, CheckCircle, Database, RefreshCw, Search, TrendingUp, User, Wrench, XCircle, Zap } from "lucide-react";
import type { TraceEvent } from "@/types/dashboard";
import type { Activity } from "./types";

/**
 * Turns raw trace events into the activity list shown in the timeline.
 * Logic unchanged from the former monolithic TraceViewer (titles translated, emoji prefixes replaced by icons).
 */
export function buildActivities(traces: TraceEvent[], question: string, sql?: string, answer?: string): Activity[] {
  const result: Activity[] = [];

  // Sort traces chronologically
  const sortedTraces = [...traces].sort((a, b) =>
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  // Find first and last timestamps
  const firstTimestamp = sortedTraces.length > 0
    ? sortedTraces[0].timestamp
    : new Date().toISOString();
  const lastTimestamp = sortedTraces.length > 0
    ? sortedTraces[sortedTraces.length - 1].timestamp
    : new Date().toISOString();

  // 1. ALWAYS add User Request first
  result.push({
    type: 'agent_call',
    title: 'Vraag van gebruiker',
    timestamp: firstTimestamp,
    status: 'info',
    icon: <User className="h-4 w-4" />,
    request: question, // Use the question from props
    metadata: {}
  });

  // 2. Process all trace events (handle both formats: event_name and type)
  for (const event of sortedTraces) {
    const eventType = event.event_name || event.type;

    // Handle historical trace format with type field - Parse SUB-AGENT calls only
    if (eventType === 'agent_response' && event.raw_response && Array.isArray(event.raw_response)) {
      // Build a map of function responses (outputs) by call ID
      const functionResponses = new Map();
      event.raw_response.forEach((agentResponse) => {
        if (agentResponse.content && agentResponse.content.parts) {
          agentResponse.content.parts.forEach((part: any) => {
            if (part.functionResponse) {
              functionResponses.set(part.functionResponse.id, part.functionResponse);
            }
          });
        }
      });

      // Accumulate ADK state as we process responses
      const adkRuntimeState: any = {};

      // Process each agent response for function calls
      event.raw_response.forEach((agentResponse: any) => {
        const parentAgent = agentResponse.author || 'Parent Agent';

        // Accumulate state from stateDelta
        if (agentResponse.actions && agentResponse.actions.stateDelta) {
          Object.assign(adkRuntimeState, agentResponse.actions.stateDelta);
        }

        // Only process responses that have function calls (sub-agents)
        if (agentResponse.content && agentResponse.content.parts) {
          // Extract any text that came BEFORE the function call (parent's reasoning)
          let parentReasoning = '';
          agentResponse.content.parts.forEach((part: any) => {
            if (part.text) {
              parentReasoning += part.text + '\n';
            }
          });

          agentResponse.content.parts.forEach((part: any) => {
            if (part.functionCall) {
              const subAgentName = part.functionCall.name;
              const callId = part.functionCall.id;
              const callArgs = part.functionCall.args || {};

              // Extract the request from callArgs if it exists
              const requestFromArgs = callArgs.request || callArgs.query || callArgs.input;

              // Find the corresponding function response (output) and format nicely
              const functionResponse = functionResponses.get(callId);
              let resultText = `Geen uitvoer vastgelegd voor ${subAgentName}`;

              if (functionResponse && functionResponse.response) {
                const resp = functionResponse.response;

                // Check for SQL query in various common fields
                if (resp.query || resp.sql || resp.generated_sql) {
                  const sqlQuery = resp.query || resp.sql || resp.generated_sql;
                  resultText = `\`\`\`sql\n${sqlQuery}\n\`\`\``;

                  // Add other fields if present
                  const otherFields = { ...resp };
                  delete otherFields.query;
                  delete otherFields.sql;
                  delete otherFields.generated_sql;

                  if (Object.keys(otherFields).length > 0) {
                    resultText += '\n\n' + Object.entries(otherFields).map(([key, val]) => {
                      return `**${key}:**\n${typeof val === 'string' ? val : JSON.stringify(val, null, 2)}`;
                    }).join('\n\n');
                  }
                }
                // Check for result/output fields
                else if (resp.result) {
                  resultText = typeof resp.result === 'string' ? resp.result : JSON.stringify(resp.result, null, 2);
                }
                // Check for tables/schemas
                else if (resp.tables || resp.schemas || resp.discovered_tables) {
                  const data = resp.tables || resp.schemas || resp.discovered_tables;
                  resultText = `**Discovered:**\n${JSON.stringify(data, null, 2)}`;
                }
                // Fallback to JSON
                else {
                  resultText = JSON.stringify(resp, null, 2);
                }
              }

              // Request = the actual request argument passed to sub-agent
              const requestText = requestFromArgs
                ? (typeof requestFromArgs === 'string' ? requestFromArgs : JSON.stringify(requestFromArgs, null, 2))
                : (parentReasoning.trim() || `Aanroep: ${subAgentName}`);

              // State = ADK runtime state at the time this sub-agent was called
              const stateSnapshot = { ...adkRuntimeState };

              result.push({
                type: 'agent_call',
                title: subAgentName,
                timestamp: agentResponse.timestamp || event.timestamp,
                status: functionResponse ? 'success' : 'info',
                icon: <Zap className="h-4 w-4" />,
                request: requestText,
                state: JSON.stringify(stateSnapshot, null, 2),
                result: resultText,
                metadata: {
                  agent_name: subAgentName,
                  parent_agent: parentAgent,
                  call_id: callId,
                  is_sub_agent: true
                }
              });
            }
          });
        }
      });
      continue;
    }

    if (eventType === 'sql_extraction' && event.sql) {
      const eventNum = sortedTraces.indexOf(event) + 1;
      result.push({
        type: 'sql_extraction',
        title: 'SQL geëxtraheerd',
        timestamp: event.timestamp,
        status: event.sql_valid ? 'success' : 'warning',
        icon: <Database className="h-4 w-4" />,
        request: `Event ${eventNum}/${sortedTraces.length}\nSQL-extractiepoging`,
        state: JSON.stringify({
          sql_found: event.sql_found,
          sql_valid: event.sql_valid,
          timestamp: event.timestamp,
          ready_for_execution: event.sql_valid
        }, null, 2),
        result: `\`\`\`sql\n${event.sql}\n\`\`\``,
        metadata: {
          sql_found: event.sql_found,
          sql_valid: event.sql_valid
        }
      });
      continue;
    }

    if (eventType === 'accuracy_evaluation') {
      const eventNum = sortedTraces.indexOf(event) + 1;
      result.push({
        type: 'evaluation',
        title: `Nauwkeurigheidsscore: ${event.score?.toFixed(1) || 'n.v.t.'}/5`,
        timestamp: event.timestamp,
        status: (event.score ?? 0) >= 4 ? 'success' : (event.score ?? 0) >= 3 ? 'warning' : 'error',
        icon: <TrendingUp className="h-4 w-4" />,
        request: `Event ${eventNum}/${sortedTraces.length}\nBeoordelingsverzoek`,
        state: JSON.stringify({
          score: event.score ?? 0,
          confidence: event.confidence || 'N/A',
          timestamp: event.timestamp,
          evaluation_complete: true
        }, null, 2),
        result: event.reasoning || 'Geen onderbouwing opgegeven',
        metadata: {
          score: event.score,
          confidence: event.confidence
        }
      });
      continue;
    }

    // Original tool_interaction format
    if (event.event_name === 'tool_interaction' && event.data?.tool_name) {
      const hasError = event.data.result_full?.includes('ERROR') ||
                      event.data.result_preview?.includes('ERROR') ||
                      event.data.result_full?.includes('EXECUTION STATUS: error');

      // Format tool name nicely
      const toolName = event.data.tool_name || 'unknown_tool';
      const toolIcon = toolName.toLowerCase().includes('bigquery') || toolName.toLowerCase().includes('query')
        ? <Search className="h-4 w-4" />
          : <Wrench className="h-4 w-4" />;

      // Extract state information - prefer state AFTER the tool completes
      let stateText = '';
      if (event.data.state_after_response && Object.keys(event.data.state_after_response).length > 0) {
        stateText = JSON.stringify(event.data.state_after_response, null, 2);
      } else if (event.data.state_at_request && Object.keys(event.data.state_at_request).length > 0) {
        stateText = JSON.stringify(event.data.state_at_request, null, 2);
      }

      result.push({
        type: 'agent_call',
        title: toolName,
        timestamp: event.timestamp,
        status: hasError ? 'error' : 'success',
        icon: toolIcon,
        request: typeof event.data.arguments === 'string'
          ? event.data.arguments
          : JSON.stringify(event.data.arguments, null, 2),
        result: event.data.result_full || event.data.result_preview || 'Geen resultaat',
        state: stateText,  // Add state information
        metadata: {
          tool_name: event.data.tool_name,
          tokens_in: event.data.tokens_in,
          tokens_out: event.data.tokens_out,
          duration_ms: event.data.duration_ms,
          sequence: event.data.sequence,
          isNested: true  // Mark as nested to indent visually
        }
      });
    }

    // Handle state_change events
    if (event.event_name === 'state_change' && event.data) {
      const stateDelta = event.data.state_delta || {};
      const stateAfter = event.data.state_after || {};

      if (Object.keys(stateDelta).length > 0 || Object.keys(stateAfter).length > 0) {
        result.push({
          type: 'agent_response',
          title: 'State bijgewerkt',
          timestamp: event.timestamp,
          status: 'info',
          icon: <RefreshCw className="h-4 w-4" />,
          state: JSON.stringify(stateDelta, null, 2),
          result: JSON.stringify(stateAfter, null, 2),
          metadata: {
            state_keys: Object.keys(stateDelta).join(', ')
          }
        });
      }
    }

    // Handle fatal exceptions with comprehensive error details
    if (event.event_name === 'fatal_exception' && event.data) {
      const errorData = event.data;
      const errorType = errorData.error_type || 'Error';
      const errorMessage = errorData.error_message || 'Unknown error';
      const shortMessage = `${errorType}: ${errorMessage}`.substring(0, 100);

      const errorDetails = `🚨 EVALUATION FAILURE REPORT

📅 WHEN:    ${errorData.timestamp || event.timestamp}
📍 WHERE:   ${errorData.location?.file || 'unknown'}:${errorData.location?.line || 0}
         Function: ${errorData.location?.function || 'unknown'}
         Code: ${errorData.location?.code || ''}

⚠️  WHAT:    ${errorType}: ${errorMessage}
❓ WHY:     ${errorData.context?.operation || 'Unknown operation'}

📋 QUESTION: ${errorData.context?.question_id || 'Unknown'}
${errorData.context?.question ? `"${errorData.context.question}"` : ''}

🔧 EXECUTION STATE:
 • Has Session: ${errorData.execution_state?.has_session ? '✓' : '✗'}
 • Has Response: ${errorData.execution_state?.has_response ? '✓' : '✗'}
 • Has Response Data: ${errorData.execution_state?.has_response_data ? '✓' : '✗'}
 • Trace File: ${errorData.execution_state?.trace_file_path || 'N/A'}

📚 STACK TRACE:
${errorData.stack_trace || 'No stack trace available'}`;

      result.push({
        type: 'agent_call',
        title: errorType,
        timestamp: event.timestamp,
        status: 'error',
        icon: <XCircle className="h-4 w-4" />,
        errorPreview: shortMessage,  // Show condensed in collapsed view
        error: errorDetails,         // Show full details when expanded
        metadata: {
          error_type: errorData.error_type,
          error_class: errorData.error_class,
          operation: errorData.context?.operation,
          question_id: errorData.context?.question_id,
          isNested: true
        }
      });
    }

    // Handle empty/invalid response errors
    if (event.event_name === 'empty_response_error' && event.data) {
      const errorData = event.data;
      const validationErrors = errorData.validation_errors || [];
      const shortMessage = validationErrors.length > 0
        ? validationErrors[0].substring(0, 80)
        : 'Leeg of ongeldig antwoord van de agent';

      const errorDetails = `🚨 EMPTY/INVALID AGENT RESPONSE

📅 Timestamp: ${errorData.timestamp || event.timestamp}

⚠️  Validation Errors:
${validationErrors.map((err: string) => `   • ${err}`).join('\n')}

📊 Response Details:
 • Response Type: ${errorData.response_type || 'unknown'}
 • Response Length: ${errorData.response_length || 0}
 • Extracted Text Length: ${errorData.extracted_text?.length || 0}
 • HTTP Status: ${errorData.http_status || 'unknown'}

📄 Response Preview:
${errorData.raw_response_preview || 'No response data'}

${errorData.extracted_text ? `📝 Extracted Text:\n${errorData.extracted_text}` : ''}`;

      result.push({
        type: 'agent_call',
        title: 'Leeg of ongeldig antwoord',
        timestamp: event.timestamp,
        status: 'error',
        icon: <XCircle className="h-4 w-4" />,
        errorPreview: shortMessage,  // Show condensed in collapsed view
        error: errorDetails,         // Show full details when expanded
        metadata: {
          validation_errors: errorData.validation_errors,
          response_type: errorData.response_type,
          isNested: true
        }
      });
    }

    // Handle content warnings (missing SQL/answer)
    if (event.event_name === 'content_warnings' && event.data) {
      const errorData = event.data;
      const warnings = errorData.warnings || [];
      const shortMessage = warnings.length > 0
        ? warnings.join(', ').substring(0, 80)
        : 'SQL of antwoord ontbreekt';

      const warningDetails = `⚠️  INCOMPLETE AGENT RESPONSE

📅 Timestamp: ${errorData.timestamp || event.timestamp}

⚠️  Warnings:
${warnings.map((warn: string) => `   • ${warn}`).join('\n')}

📊 Content Analysis:
 • Has SQL: ${errorData.has_sql ? '✓' : '✗'}
 • Has Answer: ${errorData.has_answer ? '✓' : '✗'}
 • Response Length: ${errorData.response_length || 0} chars

The agent responded but did not provide all expected content (SQL query and/or answer).`;

      result.push({
        type: 'agent_call',
        title: 'Inhoudswaarschuwingen',
        timestamp: event.timestamp,
        status: 'error',
        icon: <AlertTriangle className="h-4 w-4" />,
        request: shortMessage,   // Show condensed in collapsed view
        error: warningDetails,   // Show full details when expanded
        metadata: {
          warnings: errorData.warnings,
          has_sql: errorData.has_sql,
          has_answer: errorData.has_answer,
          isNested: true
        }
      });
    }

    // Handle agent errors (extracted from agent_response enhancement)
    if (event.event_name === 'agent_error' && event.data) {
      const errorData = event.data;
      const errorCode = errorData.error_code || 'UNKNOWN_ERROR';
      const shortMessage = errorData.error_message?.split('\n')[0]?.substring(0, 80) || 'Uitvoering van de agent mislukt';

      const errorDetails = `🔴 AGENT EXECUTION ERROR

📅 Timestamp: ${event.timestamp}

⚠️  Error: ${errorCode}
💬 Message: ${errorData.error_message || 'Agent execution failed'}
🏁 Finish Reason: ${errorData.finish_reason || 'ERROR'}
📍 Error Index: ${errorData.error_index}`;

      result.push({
        type: 'agent_call',
        title: errorCode,
        timestamp: event.timestamp,
        status: 'error',
        icon: <AlertTriangle className="h-4 w-4" />,
        request: shortMessage, // Show condensed in collapsed view
        error: errorDetails,   // Show full details when expanded
        metadata: {
          error_code: errorData.error_code,
          finish_reason: errorData.finish_reason,
          isNested: true
        }
      });
    }

    // Handle general errors
    if (event.event_name === 'error' && event.data) {
      const errorText = event.data.error || JSON.stringify(event.data, null, 2);
      const shortError = errorText.split('\n')[0]?.substring(0, 80) || 'Er is een fout opgetreden';

      const errorDetails = `❌ ERROR

📅 Timestamp: ${event.timestamp}

${errorText}`;

      result.push({
        type: 'agent_call',
        title: 'Fout',
        timestamp: event.timestamp,
        status: 'error',
        icon: <AlertTriangle className="h-4 w-4" />,
        request: shortError,  // Show condensed in collapsed view
        error: errorDetails,  // Show full details when expanded
        metadata: { isNested: true }
      });
    }

    // Handle session creation - SKIP: not useful in trace view
    // if (event.event_name === 'session_created' && event.data) {
    //   ... hidden ...
    // }

    // Handle agent execution timing - DISABLED (not shown in trace view)
    // This is now skipped as it doesn't provide useful information in the trace
    // if (event.event_name === 'agent_execution' && event.data && event.data.duration_ms != null) {
    //   ...
    // }

    // Handle SQL extraction - SKIP if no SQL found (shown in errors already)
    if (event.event_name === 'sql_extraction' && event.data && event.data.sql_found) {
      const sqlDetails = `🔍 SQL EXTRACTION

📅 Timestamp: ${event.timestamp}

SQL Found: ✓
${event.data.sql ? `\n\`\`\`sql\n${event.data.sql}\n\`\`\`` : ''}`;

      result.push({
        type: 'agent_call',
        title: 'SQL-extractie',
        timestamp: event.timestamp,
        status: 'success',
        icon: <Database className="h-4 w-4" />,
        result: sqlDetails,
        metadata: {
          sql_found: true
        }
      });
    }

    // Handle accuracy evaluation - Store for later (will be added after Final Response)
    // if (event.event_name === 'accuracy_evaluation' && event.data && event.data.score != null) {
    //   ... will be handled after the loop
    // }
  }

  // 3. Add Final Response (if we have data)
  if (answer || sql) {
    // Try to parse answer as JSON first
    let parsedAnswer = null;
    let plainAnswer = answer;

    if (answer) {
      try {
        // Try to extract JSON from markdown code block if present
        let jsonText = answer;
        const jsonBlockMatch = answer.match(/```json\s*\n([\s\S]*?)\n```/);
        if (jsonBlockMatch) {
          jsonText = jsonBlockMatch[1];
        }

        parsedAnswer = JSON.parse(jsonText);
        if (parsedAnswer && typeof parsedAnswer === 'object') {
          // It's a JSON response
          plainAnswer = parsedAnswer.answer || answer;
        }
      } catch {
        // Not JSON, use as-is
      }
    }

    // Use SQL from parsed JSON if available, otherwise from props
    const finalSql = (parsedAnswer?.sql || sql);

    // Format the final response exactly as the agent provides it
    let finalResponse = '';

    if (finalSql) {
      finalResponse += '```sql\n' + finalSql + '\n```';
      if (plainAnswer) {
        finalResponse += '\n\n';
      }
    }

    if (plainAnswer) {
      finalResponse += plainAnswer;
    }

    result.push({
      type: 'agent_response',
      title: 'Eindantwoord',
      timestamp: lastTimestamp,
      status: 'success',
      icon: <CheckCircle className="h-4 w-4" />,
      result: finalResponse,
      metadata: {}
    });
  }

  // 4. Add Evaluation last (after Final Response)
  const evalEvent = sortedTraces.find(t => t.event_name === 'accuracy_evaluation' && t.data && t.data.score != null);
  if (evalEvent && evalEvent.data) {
    const score = evalEvent.data.score.toFixed(1);
    const evalDetails = `📊 ACCURACY EVALUATION

📅 Timestamp: ${evalEvent.timestamp}

Score: ${score}/5.0
Confidence: ${evalEvent.data.confidence || 'N/A'}

${evalEvent.data.reasoning || 'Geen onderbouwing opgegeven'}`;

    result.push({
      type: 'evaluation',
      title: `Beoordeling: ${score}/5`,
      timestamp: evalEvent.timestamp,
      status: evalEvent.data.score >= 4 ? 'success' : evalEvent.data.score >= 3 ? 'info' : 'error',
      icon: <CheckCircle className="h-4 w-4" />,
      result: evalDetails,
      metadata: {
        score: evalEvent.data.score,
        confidence: evalEvent.data.confidence
      }
    });
  }

  return result;
}
